import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * AdSense ad unit. Live when REACT_APP_ADSENSE_ENABLED=true AND both the client and the
 * matching slot env var are set (see .env.production). The loader script lives in
 * public/index.html; this component just renders the <ins> and pushes it to adsbygoogle.
 * Falls back to nothing in production (or a labelled placeholder in dev) when unconfigured,
 * so an unset slot can never emit a broken ad unit.
 */
export default function AdSensePlaceholder({ slot = 'top-banner', format = 'display', sx = {} }) {
  const { t } = useTranslation();
  const insRef = useRef(null);
  const client = process.env.REACT_APP_ADSENSE_CLIENT;
  const adSlot = process.env[`REACT_APP_ADSENSE_SLOT_${slot.toUpperCase().replace('-', '_')}`];
  const enabled = process.env.REACT_APP_ADSENSE_ENABLED === 'true' && Boolean(client) && Boolean(adSlot);

  useEffect(() => {
    if (!enabled || !insRef.current) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [enabled]);

  if (enabled) {
    // Per-format <ins> attributes. Every format shares the single adsbygoogle loader
    // script in public/index.html — each unit only renders its <ins> and pushes once.
    const insProps =
      format === 'in-article'
        ? { style: { display: 'block', textAlign: 'center' }, 'data-ad-layout': 'in-article', 'data-ad-format': 'fluid' }
        : format === 'multiplex'
        ? { style: { display: 'block' }, 'data-ad-format': 'autorelaxed' }
        : { style: { display: 'block' }, 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' };
    // Reserve vertical space so the slot filling in doesn't shift content below it (CLS).
    const minHeight = format === 'multiplex' ? 280 : format === 'in-article' ? 250 : 100;
    return (
      <Box sx={{ textAlign: 'center', my: 2, minHeight, ...sx }}>
        <Typography variant="caption" color="text.disabled" display="block" mb={0.5}>
          {t('ads.label')}
        </Typography>
        <ins
          ref={insRef}
          className="adsbygoogle"
          data-ad-client={client}
          data-ad-slot={adSlot}
          {...insProps}
        />
      </Box>
    );
  }

  // Dev placeholder — invisible in production, visible in dev to mark ad positions
  if (process.env.NODE_ENV !== 'production') {
    return (
      <Box
        sx={{
          border: '2px dashed rgba(99,102,241,0.2)',
          borderRadius: 2, p: 2, my: 2,
          background: 'rgba(99,102,241,0.02)',
          textAlign: 'center',
          ...sx,
        }}
      >
        <Typography variant="caption" color="primary.light">
          AdSense slot: {slot}{format !== 'display' ? ` (${format})` : ''}
        </Typography>
      </Box>
    );
  }

  return null;
}
